import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, definirAoExpirar, definirFiltroEmpresa, definirSimulacaoDaTela, filtroEmpresaDaAba, get, mensagemDeErro, patch, post } from './api';
import { encerrarConsultas, encerrarSessao, esquecerEstiloDoWorkspace } from './saida';
import i18n from 'i18next';
import { useToast } from '../componentes/toast';
import { trocarIdioma } from '../i18n';
import type { Idioma } from './formato';
import { aplicarFavicon, aplicarFonte, fonteEfetiva } from './fontes';
import { aplicarEstilo, aplicarModelo, ESTILO_MODELO, FONTE_DO_ESTILO, type Estilo } from './estilos';
import { conferirVoltaDoBfcache } from './volta-bfcache';
import { estiloEfetivo, type ConfigSubsistemas, type ModeloTema } from '@malgoos/shared';
import { CtxSessao } from './sessao-contexto';

export interface DadosSessao {
  usuario: {
    id: string;
    nome: string;
    /** val1 (28/10): vazio na conta sem e-mail (acesso só pelo nome de usuário). */
    email: string;
    /** val1 (28/10): nome de usuário do login (global, sem "@"). */
    usuario?: string | null;
    sem_email?: boolean;
    /** Lista de 30/10 (item 11): as formas de acesso ativas da conta (ausente em servidor anterior = as duas). */
    login_email?: boolean;
    login_usuario?: boolean;
    /** Lista de 30/10 (item 6): versão em uso da foto do perfil (null = sem foto, iniciais). */
    avatar_versao?: string | null;
    idioma: Idioma;
    tema: 'escuro' | 'claro';
    /** Estilos de interface (27/10): o estilo escolhido pela pessoa (ausente em servidor anterior = Malgo). */
    estilo?: Estilo | null;
    /** N5 (28/10): estilo fixado pelo administrador neste workspace e o que vale (a regra de `estiloEfetivo`). */
    estilo_fixado?: Estilo | null;
    estilo_efetivo?: Estilo;
    onboarding_concluido: boolean;
    plataforma_admin: boolean;
    /** Fase 2 (W4): fonte pessoal (null segue a do workspace) e escala de texto. */
    fonte?: string | null;
    escala_texto?: 100 | 110 | 125 | 140 | 150;
    /** U15 F5: modo simples da pessoa neste workspace (desligado por padrão; vive no vínculo com o workspace). */
    modo_simples?: boolean;
    /** U15 F2: telas cuja dica de primeira visita a pessoa já viu (chaves de tela). */
    dicas_vistas?: string[];
  };
  workspace: { id: string; nome: string; slug: string; fuso: string; moeda: string; sandbox: boolean; tenant_nome: string; tenant_id: string };
  workspaces: { tenant_id: string; tenant_nome: string; workspace_id: string; workspace_nome: string; sandbox: boolean; padrao?: boolean }[];
  permissoes: string[];
  /**
   * Val1 (papel por sub-sistema, 28/10): páginas (`<slug>.<aba>`) desmarcadas no papel ou retiradas da pessoa. Somem do menu
   * e da rota (a API responde 403 nas rotas só delas). Ausente (sessão antiga em cache): nenhuma.
   */
  paginas_negadas?: string[];
  modulos: { slug: string; nome: string; menu: { chave: string; ordem: number }; abas: string[] }[];
  /** Gravada pelo módulo Configurações na chave `aparencia`. */
  aparencia: {
    /** Fase 2 (W32): verde é a paleta Malgo, o padrão (sem `data-acento`). */
    acento?: 'verde' | 'rosa' | 'roxo' | 'azul' | 'ciano' | 'laranja';
    tema_padrao?: 'escuro' | 'claro';
    idioma_padrao?: Idioma;
    /** Fase 2 (W4): fonte do texto do workspace, permissão da fonte pessoal e marca (nome e hash da logo). */
    fonte?: string;
    permitir_fonte_pessoal?: boolean;
    /** N5 (28/10): estilo de interface do workspace e a escolha pessoal liberada. */
    estilo_padrao?: Estilo;
    permitir_estilo_pessoal?: boolean;
    /** Modelos próprios do workspace e o que está em uso (03/11). */
    modelos?: ModeloTema[];
    modelo_ativo?: string | null;
    marca_nome?: string | null;
    marca_logo?: string | null;
  } | null;
  /** Fase 2 (W4): verificação em duas etapas da pessoa (o segredo nunca vem). */
  seguranca?: { mfa_ativo: boolean; mfa_obrigatorio: boolean; mfa_prazo: string | null; mfa_restrita: boolean; troca_senha?: boolean };
  /** Fase 2, W2a: empresa atual, "Todas as empresas" (com `nucleo.consolidado`) e as empresas que o seletor oferece. */
  empresas?: EmpresasDaSessao;
  versao: string;
  /** Fase 2 (W1): ambiente do servidor (`APP_AMBIENTE`); fora da produção, a faixa vermelha aparece. */
  ambiente?: 'producao' | 'homologacao' | 'staging';
  /** Fase 2 (W8): modo de operação efetivo de cada módulo instalado (`{ "compras.modo": "simplificado" }`). */
  modos?: Record<string, string>;
  /**
   * Lote de 19/10 (item 9): campos nativos escondidos ou obrigatórios por tela (`{ "cadastros.parceiro": { "email":
   * { "visivel": true, "obrigatorio": true } } }`), só o que difere do padrão. Lido pelo `useCamposDaTela`.
   */
  campos_telas?: Record<string, Record<string, { visivel: boolean; obrigatorio: boolean }>>;
  /** D1 (sub-sistemas): a arrumação do workspace e a de cada empresa (só as diferenças do padrão; ausente = a de sempre). */
  subsistemas?: { workspace: ConfigSubsistemas; empresas: Record<string, ConfigSubsistemas> };
  /**
   * val4 (29/10): simulação de usuário de teste em curso nesta sessão (`usuario`, `permissoes` e o resto já são os da conta
   * simulada); null ou ausente fora dela. `ator_nome` é quem simula; `perfil_permitido` false: o papel da conta mudou e as
   * ações ficaram bloqueadas.
   */
  simulacao?: { id: string; ator_id: string; ator_nome: string; expira_em: string; perfil_permitido: boolean } | null;
}

export interface EmpresasDaSessao {
  /** Empresa atual; nula em "Todas as empresas". */
  atual: string | null;
  todas: boolean;
  consolidado: boolean;
  opcoes: { id: string; nome: string }[];
  /**
   * Review r41 visual (M1, B1): quem pede vê todas as empresas do workspace (arquivadas inclusive). Quem não vê não cadastra
   * nem arquiva empresa e não deixa vazia a lista de empresas de um membro. Ausente (servidor anterior) = vê.
   */
  ve_todas?: boolean;
  /** Val1 (28/10): o workspace trabalha com troca de empresa/vertical (seletor no perfil). Ausente (servidor anterior) = sim. */
  troca?: boolean;
}

/** Val1: com a troca desligada o workspace é um ambiente único (sem seletor de empresa; sempre "Minhas verticais"). */
export const trocaDeEmpresaLigada = (s: Pick<DadosSessao, 'empresas'>) => s.empresas?.troca !== false;

/** Review r41 visual (M1, B1): o alcance de empresas de quem está na sessão (servidor anterior, sem o campo: vê todas). */
export const veTodasAsEmpresas = (s: Pick<DadosSessao, 'empresas'>) => s.empresas?.ve_todas !== false;
/**
 * Review r45: configuração que vale para o workspace inteiro (modos, MCP, campos, modelos, aparência, régua, Asaas,
 * lembretes, Relatórios, Folha, termo do Portal, webhooks) só muda por quem vê todas as empresas (o servidor recusa com
 * `permissao.padrao_workspace_exige_todas`); a tela trata o restrito como quem não tem a permissão.
 */
export const podeNoWorkspace = (s: Pick<DadosSessao, 'empresas'> & { pode(permissao: string): boolean }, permissao: string) =>
  s.pode(permissao) && veTodasAsEmpresas(s);

export interface ValorSessao extends DadosSessao {
  pode(permissao: string): boolean;
  /** Val1: a página (aba `slug.aba`) não foi desmarcada no papel nem retirada da pessoa. */
  paginaLiberada(slug: string, aba: string): boolean;
  /** Fase 2 (W37): administrador da plataforma (abas `plataforma`, como o Histórico telas). */
  plataforma: boolean;
  /** Lote 19: vê todas as empresas do workspace e tem o consolidado (abas `todasAsEmpresas`, como o Painel do grupo). */
  todasAsEmpresas: boolean;
  /** U15 F5: modo simples ligado (esconde as abas avançadas da navegação). */
  modoSimples: boolean;
  instalado(slug: string): boolean;
  /** Fase 2 (W8): valor do modo de operação (`compras.modo`); `undefined` quando o módulo não está instalado. */
  modo(chave: string): string | undefined;
  trocarWorkspace(id: string): Promise<void>;
  /** CRM novo desenho F0 (N1): workspace de entrada no login (a própria pessoa escolhe entre os seus). */
  definirWorkspacePadrao(id: string): Promise<void>;
  sair(): Promise<void>;
  /** val4 (29/10): simula a conta de teste (a página recarrega como ela) e "Voltar para mim" (recarrega como quem simula). */
  simular(usuarioId: string): Promise<void>;
  voltarDaSimulacao(): Promise<void>;
  /** Review r23 robustez (O1): a saída está em voo (o botão Sair fica desligado; um clique a mais não manda outro POST). */
  saindo: boolean;
  preferencias(p: Partial<Pick<DadosSessao['usuario'], 'idioma' | 'tema' | 'estilo' | 'nome' | 'onboarding_concluido' | 'fonte' | 'escala_texto'>>): Promise<void>;
  recarregar(): Promise<unknown>;
}

const Ctx = CtxSessao;

export function aplicarTema(tema: 'escuro' | 'claro') {
  document.documentElement.dataset.tema = tema;
  try {
    localStorage.setItem('malgoos.tema', tema);
  } catch {
    /* sem storage */
  }
}

/** Fase 2 (W32): a paleta Malgo é o acento padrão; ela vale sem o atributo `data-acento` no <html>. */
export const ACENTO_PADRAO = 'verde';

/** Acento da empresa para o `public/tema.js` (r8 visual B10); verde Malgo (padrão desde a W32) ou ausente limpa a chave. */
export function guardarAcento(acento: string | null | undefined) {
  try {
    if (acento && acento !== ACENTO_PADRAO) localStorage.setItem('malgoos.acento', acento);
    else localStorage.removeItem('malgoos.acento');
  } catch {
    /* sem storage */
  }
}

/** Consulta da sessão; o pós-login (W32) a carrega durante o splash, antes de navegar. */
export const opcoesSessao = { queryKey: ['sessao'], queryFn: () => get<DadosSessao>('/auth/sessao'), retry: false, staleTime: 60_000 } as const;

export function useSessaoQuery() {
  return useQuery(opcoesSessao);
}

export function ProvedorSessao({ dados, children }: { dados: DadosSessao; children: ReactNode }) {
  const qc = useQueryClient();
  const navegar = useNavigate();

  // R-B6 (r8 robustez): tema e idioma escolhidos e ainda não confirmados pelo servidor valem na tela e nos controles (o
  // botão alterna a partir do que está aplicado, não do valor antigo do servidor).
  const [otimista, setOtimista] = useState<Partial<Pick<DadosSessao['usuario'], 'tema' | 'idioma'>>>({});
  const usuario = useMemo(() => ({ ...dados.usuario, ...otimista }), [dados.usuario, otimista]);
  useEffect(() => {
    aplicarTema(usuario.tema);
    void trocarIdioma(usuario.idioma);
  }, [usuario.tema, usuario.idioma]);

  // Impressão (r6 robustez B2): papel sai no tema claro (no escuro o texto claro some no papel sem fundo); volta depois.
  useEffect(() => {
    let antes: string | undefined;
    const imprimir = () => {
      antes = document.documentElement.dataset.tema;
      document.documentElement.dataset.tema = 'claro';
    };
    const voltar = () => {
      if (antes) document.documentElement.dataset.tema = antes;
      antes = undefined;
    };
    window.addEventListener('beforeprint', imprimir);
    window.addEventListener('afterprint', voltar);
    return () => {
      window.removeEventListener('beforeprint', imprimir);
      window.removeEventListener('afterprint', voltar);
    };
  }, []);

  useEffect(() => {
    const acento = dados.aparencia?.acento;
    if (acento && acento !== ACENTO_PADRAO) document.documentElement.dataset.acento = acento;
    else delete document.documentElement.dataset.acento;
    // r8 visual B10: guardado para o tema.js aplicar antes do paint na próxima carga (vale também ao trocar de workspace).
    guardarAcento(acento);
  }, [dados.aparencia?.acento]);

  // Val1 (28/10): com a troca de empresa desligada, um filtro de empresa que sobrou desta aba (de antes de desligar) sai uma vez;
  // depois dele, a escolha da "Empresa do registro" nos formulários segue por cabeçalho, sem filtro na aba.
  const limpouFiltro = useRef(false);
  useEffect(() => {
    if (limpouFiltro.current || dados.empresas?.troca !== false) return;
    limpouFiltro.current = true;
    if (filtroEmpresaDaAba()) {
      definirFiltroEmpresa(null);
      void qc.invalidateQueries();
    }
  }, [dados.empresas?.troca, qc]);

  // Estilos de interface (27/10): o estilo da pessoa no <html> (a Malgo sem o atributo), guardado para o tema.js.
  // N5 (28/10): fixado pelo admin > escolha da pessoa (se permitida) > padrão do workspace > Maré (desde 03/11).
  const estilo = estiloEfetivo({ fixado: dados.usuario.estilo_fixado, pessoa: dados.usuario.estilo, permitirPessoal: dados.aparencia?.permitir_estilo_pessoal, padrao: dados.aparencia?.estilo_padrao }) as Estilo;
  useEffect(() => {
    aplicarEstilo(estilo);
  }, [estilo]);
  // Modelo próprio (03/11): as cores do modelo ativo valem só para quem está no estilo `proprio`.
  const modeloAtivo = estilo === ESTILO_MODELO ? (dados.aparencia?.modelos?.find((m) => m.id === dados.aparencia?.modelo_ativo) ?? null) : null;
  useEffect(() => {
    aplicarModelo(modeloAtivo);
  }, [modeloAtivo]);

  // Fase 2 (W4): fonte do texto (a da pessoa, se o admin permitir, senão a do estilo escolhido, senão a do workspace), escala e
  // favicon da marca.
  const fonte = fonteEfetiva(dados.aparencia, dados.usuario.fonte, FONTE_DO_ESTILO[estilo] ?? null);
  useEffect(() => {
    aplicarFonte(fonte, dados.usuario.escala_texto ?? 100);
  }, [fonte, dados.usuario.escala_texto]);
  useEffect(() => {
    aplicarFavicon(dados.aparencia?.marca_logo);
  }, [dados.aparencia?.marca_logo]);

  // Review r24 spec (M1): com a saída em andamento, um 401 atrasado (pedido que saiu antes do logout) não leva ao /login; quem
  // decide o destino é o `sair` (a tela de saída, ou o app de volta com o aviso quando a saída falha).
  const saindoRef = useRef(false);
  useEffect(() => {
    definirAoExpirar(() => {
      if (saindoRef.current) return;
      esquecerEstiloDoWorkspace();
      qc.clear();
      // Review r29 spec (O2): a aba que perde a troca de senha (ou a sessão encerrada em outro lugar) chega ao login com o
      // aviso, em vez de cair nele sem explicação.
      navegar('/login?msg=sessao_encerrada', { replace: true });
    });
  }, [qc, navegar]);

  // Fase 2 (W32): a tela do app restaurada do cache de ida e volta do navegador (bfcache) fica escondida até o servidor
  // confirmar a sessão; sem ela (saiu por outra página, 401), vai ao login por carga nova, sem o que estava na memória.
  // Review r23 segurança (O2): com a rede falhando, segue escondida e tenta de novo (`conferirVoltaDoBfcache`).
  useEffect(() => {
    const aoMostrar = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      void conferirVoltaDoBfcache({
        raiz: document.getElementById('raiz'),
        conferir: () => fetch('/api/v1/auth/sessao', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }),
        irAoLogin: () => window.location.replace('/login'),
      });
    };
    window.addEventListener('pageshow', aoMostrar);
    return () => window.removeEventListener('pageshow', aoMostrar);
  }, []);

  // val4 (29/10): a simulação que a tela mostra vai no `X-Simulacao` de toda requisição (a API recusa a da simulação que acabou).
  definirSimulacaoDaTela(dados.simulacao?.id ?? null);

  const permissoes = useMemo(() => new Set(dados.permissoes), [dados.permissoes]);
  const negadas = useMemo(() => new Set(dados.paginas_negadas ?? []), [dados.paginas_negadas]);
  const instalados = useMemo(() => new Set(dados.modulos.map((m) => m.slug)), [dados.modulos]);

  // r6 robustez B4: com o PATCH falhando, a tela trocava de tema ou idioma, nada ficava salvo, não havia aviso e sobrava uma
  // promise rejeitada. Agora o tema e o idioma voltam ao que estava salvo, o aviso aparece e a promise resolve.
  // R-B6 (r8 robustez): os PATCHes saem em fila (na ordem dos cliques) e cada campo tem um contador de geração; a falha de
  // uma escolha já superada por outra não desfaz nada (antes o rollback do 1º, lento, sobrescrevia o 2º, que deu certo).
  const toast = useToast();
  const salvos = useRef({ tema: dados.usuario.tema, idioma: dados.usuario.idioma });
  salvos.current = { tema: dados.usuario.tema, idioma: dados.usuario.idioma };
  const avisar = useRef(toast.erro);
  const [saindo, setSaindo] = useState(false);
  avisar.current = toast.erro;
  const geracao = useRef({ tema: 0, idioma: 0 });
  const fila = useRef<Promise<unknown>>(Promise.resolve());
  const preferencias = useCallback<ValorSessao['preferencias']>(
    async (p) => {
      const campos = (['tema', 'idioma'] as const).filter((k) => p[k]);
      const minhas = { tema: 0, idioma: 0 };
      for (const k of campos) minhas[k] = ++geracao.current[k];
      const vigente = (k: 'tema' | 'idioma') => geracao.current[k] === minhas[k];
      const soltar = (ks: readonly ('tema' | 'idioma')[]) =>
        ks.length &&
        setOtimista((o) => {
          const n = { ...o };
          for (const k of ks) delete n[k];
          return n;
        });
      if (campos.length) setOtimista((o) => ({ ...o, ...(p.tema ? { tema: p.tema } : {}), ...(p.idioma ? { idioma: p.idioma } : {}) }));
      if (p.tema) aplicarTema(p.tema);
      if (p.idioma) await trocarIdioma(p.idioma);
      const envio = fila.current.then(() => patch('/conta/preferencias', p));
      fila.current = envio.catch(() => undefined);
      try {
        await envio;
      } catch (e) {
        const voltar = campos.filter(vigente);
        // Superada por outra escolha do mesmo campo (ou só campos sem tela, como o nome): nada a desfazer aqui.
        if (!campos.length || voltar.length) {
          soltar(voltar);
          if (voltar.includes('tema')) aplicarTema(salvos.current.tema);
          if (voltar.includes('idioma')) await trocarIdioma(salvos.current.idioma);
          avisar.current(mensagemDeErro(e));
        }
        return;
      }
      await qc.invalidateQueries({ queryKey: ['sessao'] });
      soltar(campos.filter(vigente));
    },
    [qc],
  );

  const valor: ValorSessao = {
    ...dados,
    usuario,
    pode: (p) => permissoes.has(p),
    paginaLiberada: (slug, aba) => !negadas.has(`${slug}.${aba}`),
    plataforma: !!usuario.plataforma_admin,
    todasAsEmpresas: !!dados.empresas?.consolidado && veTodasAsEmpresas(dados),
    modoSimples: !!usuario.modo_simples,
    instalado: (s) => instalados.has(s),
    modo: (chave) => dados.modos?.[chave],
    async trocarWorkspace(id) {
      await post('/auth/workspace', { workspace_id: id });
      qc.clear();
      navegar('/', { replace: true });
      window.location.reload();
    },
    async definirWorkspacePadrao(id) {
      await post('/auth/workspace/padrao', { workspace_id: id });
      await qc.invalidateQueries({ queryKey: ['sessao'] });
    },
    // Fase 2 (W32): sessão encerrada no servidor e cache limpo ANTES da tela de saída (nucleo/saida.ts).
    // Review r23 robustez (O1): um pedido por vez, com prazo; enquanto está em voo, `saindo` desliga o botão.
    // Review r24 spec (M1): as consultas são desligadas antes de limpar o cache (`encerrarConsultas`: nenhum GET depois do
    // logout) e, quando a saída dá certo, `saindo` NÃO volta: devolver o estado renderizava o app de novo com o cache vazio
    // e as consultas pediam outra vez (401 e /login no lugar da despedida). Só a falha devolve o botão.
    async sair() {
      if (saindoRef.current) return;
      saindoRef.current = true;
      setSaindo(true);
      let r: 'saiu' | 'falhou' = 'falhou';
      try {
        r = await encerrarSessao({ encerrarNoServidor: () => post('/auth/logout'), limparCache: () => encerrarConsultas(qc), navegar: (para) => navegar(para, { replace: true }) });
      } finally {
        if (r === 'falhou') {
          saindoRef.current = false;
          setSaindo(false);
          avisar.current(i18n.t('saida.erro'));
        }
      }
    },
    // val4 (29/10): a simulação troca a conta da sessão no servidor; a página recarrega do zero (tema, idioma, menus e cache
    // da outra conta não sobram).
    async simular(usuarioId) {
      await post('/auth/simulacao', { usuario_id: usuarioId });
      qc.clear();
      window.location.assign('/');
    },
    async voltarDaSimulacao() {
      await api('DELETE', '/auth/simulacao');
      definirSimulacaoDaTela(null);
      qc.clear();
      window.location.assign('/');
    },
    saindo,
    preferencias,
    recarregar: () => qc.invalidateQueries({ queryKey: ['sessao'] }),
  };
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

/** D2: a sessão quando existe (componentes do núcleo, como as abas, também rodam fora dela nos testes). */
export function useSessaoOpcional() {
  return useContext(Ctx);
}

export function useSessao() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSessao fora do ProvedorSessao');
  return v;
}
